import * as turf from '@turf/turf';
import type { Feature, GeoJsonProperties, Geometry, Polygon } from 'geojson';
import { cloneDeep } from 'lodash';
import type { RegionalOverviewSource, RiskLevelEnum } from 'querys/regional-overview/regional-overview.query.models';
import type { StatisticItem } from '../components/statistics-list/statistics-list.models';
import { RISK_PRIORITY } from '../regional-overview.constants';
import type { RegionalOverviewConfiguration, RegionalOverviewConfigurationItem, RegionalOverviewLevel } from '../regional-overview.models';

export const getConfigItemByModel = (
  configs: RegionalOverviewConfiguration[],
  model?: RegionalOverviewSource
): RegionalOverviewConfigurationItem | undefined => {
  return configs.flatMap(config => config.items).find(item => item.model === model);
};

export const getConfigByModel = (
  configs: RegionalOverviewConfiguration[],
  model?: RegionalOverviewSource
): RegionalOverviewConfiguration | undefined => {
  return configs.find(config => config.items.some(item => item.model === model));
};

/**
 * Calculates statistics for a property in a GeoJSON feature collection.
 *
 * @example
 * // For simple top-level properties:
 * getStatistics(features, props => props.someProperty, levels);
 *
 * @example
 * // For nested properties:
 * getStatistics<AbioticStressProperties>(
 *   features,
 *   props => props.values.risk,
 *   abioticStressLevels
 * );
 *
 * @example
 * // With transformations:
 * getStatistics(features, props => props.risk > 5 ? 'high' : 'low', levels);
 */
export const getStatistics = <T extends GeoJsonProperties>(
  features: Feature<Geometry, T>[],
  propertyAccessor: (properties: T) => string | number | undefined,
  levels: RegionalOverviewLevel[]
): StatisticItem[] => {
  if (!features?.length) return [];

  const propertyCount = new Map<string, number>();
  const total = features.length;

  for (const item of features) {
    if (!item.properties) continue;

    const propValue = propertyAccessor(item.properties);
    if (propValue === undefined) continue;

    const stringValue = String(propValue);
    const count = propertyCount.get(stringValue) ?? 0;
    propertyCount.set(stringValue, count + 1);
  }

  const sortedStatistics = Array.from(propertyCount.entries()).sort((a, b) => b[1] - a[1]);
  const results = sortedStatistics.map(([risk, count]) => {
    const level = levels.find(l => l.value === risk);
    return {
      name: level?.type ?? '',
      percentage: ((count / total) * 100).toFixed(2),
      legendColor: level?.legend_color
    };
  });

  return results;
};

export const getRiskLevelTranslationKey = (riskType: string): string => {
  return `pages.regional_overview.regional_risk.level_risk.${riskType}`;
};

export const isFeatureWithinBounds = (feature: Feature<Geometry>, bboxPolygon: Feature<Polygon>): boolean => {
  try {
    const geometryType = feature.geometry.type;

    switch (geometryType) {
      case 'Point':
        return turf.booleanPointInPolygon(feature.geometry.coordinates, bboxPolygon);

      case 'MultiPoint':
        return feature.geometry.coordinates.some(coord => turf.booleanPointInPolygon(coord, bboxPolygon));

      case 'LineString':
      case 'MultiLineString':
      case 'Polygon':
      case 'MultiPolygon':
        return turf.booleanIntersects(feature, bboxPolygon);

      default:
        return false;
    }
  } catch (error) {
    console.error('Error checking if feature is within bounds:', error, feature);
    return false;
  }
};

export const filterFeaturesByBounds = <T extends GeoJsonProperties>(
  features: Feature<Geometry, T>[],
  bounds: mapboxgl.LngLatBounds
): Feature<Geometry, T>[] => {
  const boundingBbox = turf.bboxPolygon([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]);

  const filtered = features.filter(feature => {
    return isFeatureWithinBounds(feature, boundingBbox);
  });

  return filtered;
};

export const buildFieldDetailsUrl = (
  orgId: string,
  propertyId: string,
  fieldId: string,
  { hasError, isEmbedded }: { hasError: boolean; isEmbedded: boolean }
): string => {
  const diseaseRiskPath = isEmbedded ? 'disease-risk-by-property' : 'disease-risk';
  const configurationPath = hasError ? '/configuration' : '';
  const path = `/company/${orgId}/property/${propertyId}/${diseaseRiskPath}${configurationPath}`;

  if (hasError && !isEmbedded) return path;

  return `${path}?${new URLSearchParams({ selected: fieldId }).toString()}`;
};

export const sortFeaturesByRiskLevel = <T extends Geometry, U extends GeoJsonProperties>(
  features: Feature<T, U>[],
  riskAccessor: (properties: U) => number | undefined
): Feature<T, U>[] => {
  const clonedFeatures = cloneDeep(features);
  return [...clonedFeatures].sort((a, b) => {
    const riskA = (riskAccessor(a.properties) ?? -1) as RiskLevelEnum;
    const riskB = (riskAccessor(b.properties) ?? -1) as RiskLevelEnum;

    return RISK_PRIORITY[riskB] - RISK_PRIORITY[riskA];
  });
};
